import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, MapPin, Phone, Mail, Globe, Euro, Image as ImageIcon, X, Plus, Eye, Lock, Users, User as UserIcon, LogIn, Sparkles, Search, Map as MapIcon, LayoutGrid } from 'lucide-react';
import FranceChoroplethMap from '@/components/FranceChoroplethMap';
import EventMap, { CATEGORY_META } from '@/components/EventMap';
import { useAuth } from '@/contexts/AuthContext';
import { useEventSearch } from '@/hooks/useEventSearch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SEARCH_FIELDS = ['name', 'organizer', 'city', 'address', 'postal_code', 'department', 'description', 'disciplines'];

function formatDateRange(start, end) {
  if (!start && !end) return 'Date à confirmer';
  const opts = { day: '2-digit', month: 'long', year: 'numeric' };
  const fmt = (s) => {
    try { return new Date(s).toLocaleDateString('fr-FR', opts); } catch { return s; }
  };
  if (start && end && start !== end) return `${fmt(start)} → ${fmt(end)}`;
  return fmt(start || end);
}

function EventCard({ evt, onShowPoster }) {
  const hasPoster = !!evt.poster_url;
  const isPdf = evt.poster_mime === 'application/pdf' || (evt.poster_url || '').toLowerCase().endsWith('.pdf');
  const today = new Date().toISOString().slice(0, 10);
  const isFeatured = !!evt.featured && (!evt.featured_until || evt.featured_until >= today);

  return (
    <article
      className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#161616] to-[#0F0F0F] border transition-all duration-300 ${isFeatured ? 'border-[#FFC107]/60 shadow-[0_0_28px_-12px_rgba(255,193,7,0.5)] hover:border-[#FFC107]' : 'border-white/5 hover:border-[#FFC107]/30'}`}
      data-testid={`country-event-card-${evt.id}`}
      data-featured={isFeatured ? '1' : '0'}
    >
      {isFeatured && (
        <div
          className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black text-[10px] font-black uppercase tracking-wider shadow-lg"
          data-testid={`country-event-featured-badge-${evt.id}`}
        >
          <Sparkles className="w-3 h-3" /> À la une
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-4 p-5">
        {/* Poster */}
        {hasPoster && (
          <button
            onClick={() => onShowPoster(evt)}
            className="relative shrink-0 w-full sm:w-32 h-40 sm:h-40 rounded-xl overflow-hidden bg-black/40 border border-white/5 hover:border-[#FFC107]/50 transition-colors"
            data-testid={`country-event-poster-${evt.id}`}
          >
            {isPdf ? (
              <div className="flex flex-col items-center justify-center w-full h-full text-[#FFC107]">
                <ImageIcon className="w-10 h-10 mb-1" />
                <span className="text-[10px] uppercase tracking-widest">Affiche PDF</span>
              </div>
            ) : (
              <img
                src={`${API.replace('/api','')}${evt.poster_url}`}
                alt={`Affiche ${evt.name}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            )}
          </button>
        )}

        <div className="flex-1 min-w-0">
          <h3 className="font-['Manrope'] text-xl font-bold text-white leading-tight mb-1.5 truncate">
            {evt.name}
          </h3>
          {evt.organizer && (
            <p className="text-sm text-[#FFC107]/90 font-medium mb-2.5">{evt.organizer}</p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-[#C8C8C8]">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-[#FFC107]" />
              {formatDateRange(evt.date_start, evt.date_end)}
            </span>
            {(evt.city || evt.address) && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#FF6B35]" />
                {evt.city || evt.address}
                {evt.department && ` (${evt.department})`}
              </span>
            )}
          </div>

          {(evt.disciplines || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {evt.disciplines.map((d) => (
                <span key={d} className="px-2 py-0.5 text-[11px] rounded-full bg-[#FFC107]/10 text-[#FFC107] border border-[#FFC107]/20">
                  {d}
                </span>
              ))}
            </div>
          )}

          {evt.price_info && (
            <p className="mt-3 text-sm text-white/80 inline-flex items-center gap-1.5">
              <Euro className="w-3.5 h-3.5 text-[#FFC107]" />
              {evt.price_info}
            </p>
          )}

          {evt.description && (
            <p className="mt-3 text-sm text-[#C8C8C8] leading-relaxed line-clamp-3">{evt.description}</p>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {evt.phone && (
              <a href={`tel:${evt.phone.replace(/\s/g,'')}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-white/5 hover:bg-[#FFC107]/20 text-white transition-colors" data-testid={`country-event-phone-${evt.id}`}>
                <Phone className="w-3 h-3" /> {evt.phone}
              </a>
            )}
            {evt.email && (
              <a href={`mailto:${evt.email}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-white/5 hover:bg-[#FFC107]/20 text-white transition-colors" data-testid={`country-event-email-${evt.id}`}>
                <Mail className="w-3 h-3" /> Écrire
              </a>
            )}
            {evt.website && (
              <a href={evt.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-white/5 hover:bg-[#FFC107]/20 text-white transition-colors" data-testid={`country-event-website-${evt.id}`}>
                <Globe className="w-3 h-3" /> Site
              </a>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function PosterModal({ evt, onClose }) {
  if (!evt) return null;
  const isPdf = evt.poster_mime === 'application/pdf' || (evt.poster_url || '').toLowerCase().endsWith('.pdf');
  const src = `${API.replace('/api','')}${evt.poster_url}`;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="poster-modal"
    >
      <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white" aria-label="Fermer" data-testid="poster-modal-close">
        <X className="w-5 h-5" />
      </button>
      <div className="max-w-4xl w-full max-h-[90vh] rounded-xl overflow-hidden bg-[#0A0A0A]" onClick={(e) => e.stopPropagation()}>
        {isPdf ? (
          <iframe title={`Affiche ${evt.name}`} src={src} className="w-full h-[80vh]" />
        ) : (
          <img src={src} alt={`Affiche ${evt.name}`} className="w-full h-auto max-h-[85vh] object-contain" />
        )}
      </div>
    </div>
  );
}

export default function BallTrapCampagne() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ by_department: {}, total: 0 });
  const [events, setEvents] = useState([]);
  const [visits, setVisits] = useState(null);
  const [accountsCount, setAccountsCount] = useState(null);
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedDeptName, setSelectedDeptName] = useState('');
  const [posterEvent, setPosterEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  // Map view: 'interactive' (Leaflet markers) or 'departments' (choropleth).
  const [mapView, setMapView] = useState('interactive');
  // Active category filters (Set). Empty Set = all categories visible.
  const [activeCats, setActiveCats] = useState(new Set(Object.keys(CATEGORY_META)));
  const toggleCat = (key) => {
    setActiveCats((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Apply dept filter first, then category filter, then run debounced text search on the result.
  const deptFiltered = useMemo(() => {
    if (!selectedDept) return events;
    return events.filter((e) => e.department === selectedDept);
  }, [events, selectedDept]);

  const catFiltered = useMemo(() => {
    if (!activeCats || activeCats.size === 0) return [];
    if (activeCats.size >= Object.keys(CATEGORY_META).length) return deptFiltered;
    return deptFiltered.filter((e) => activeCats.has(e.category || 'ball_trap_campagne'));
  }, [deptFiltered, activeCats]);

  const { query, setQuery, debouncedQuery, filtered } = useEventSearch(catFiltered, SEARCH_FIELDS);

  useEffect(() => {
    let mounted = true;
    const alreadyCounted = sessionStorage.getItem('cp_btc_visit_counted');
    const trackPromise = alreadyCounted
      ? axios.get(`${API}/country-events/visits`)
      : axios.post(`${API}/country-events/track-visit`).then((r) => {
          try { sessionStorage.setItem('cp_btc_visit_counted', '1'); } catch (_) {}
          return r;
        });
    Promise.all([
      axios.get(`${API}/country-events/stats/by-department`),
      axios.get(`${API}/country-events?categories=ball_trap_campagne,concours,stand_de_tir,armurerie`),
      trackPromise,
      axios.get(`${API}/auth/stats`).catch(() => ({ data: null })),
    ])
      .then(([s, e, v, a]) => {
        if (!mounted) return;
        setStats(s.data || { by_department: {}, total: 0 });
        setEvents(Array.isArray(e.data) ? e.data : []);
        setVisits(typeof v.data?.count === 'number' ? v.data.count : null);
        setAccountsCount(typeof a.data?.accounts_count === 'number' ? a.data.accounts_count : null);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  // Pre-compute a normalized search haystack per event ONCE. Recomputes only
  // when `events` changes — keystrokes don't re-normalize 500 strings.
  // (replaced by useEventSearch hook)

  const topDepts = useMemo(() => {
    const arr = Object.entries(stats.by_department || {}).sort((a, b) => b[1] - a[1]).slice(0, 5);
    return arr;
  }, [stats]);

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-['Manrope']">
      {/* Subtle amber gradient background */}
      <div className="fixed inset-0 -z-10 pointer-events-none opacity-40"
        style={{
          background: 'radial-gradient(circle at 20% 0%, rgba(255,193,7,0.12), transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,107,53,0.08), transparent 50%)',
        }} />

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0D0D0D]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4" /> Retour
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <Link
                to="/mon-espace"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30 text-xs font-medium"
                data-testid="my-space-link"
              >
                <UserIcon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Mon espace</span>
              </Link>
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30 text-xs font-medium"
                data-testid="login-link-header"
              >
                <LogIn className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Connexion</span>
              </Link>
            )}
            <Link
              to="/admin/country-events"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-white/60 hover:text-white hover:border-white/30 text-xs font-medium transition-colors"
              data-testid="admin-link-header"
              title="Espace administrateur"
            >
              <Lock className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Admin</span>
            </Link>
            <Link
              to="/ball-trap-campagne/soumettre"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black text-sm font-bold hover:scale-105 transition-transform"
              data-testid="submit-cta-header"
            >
              <Plus className="w-4 h-4" /> Déclarer un ball-trap
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-24">
        {/* Hero */}
        <section className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFC107]/10 border border-[#FFC107]/20 text-[#FFC107] text-xs font-semibold mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFC107] animate-pulse" />
            Nouveauté · Juin 2026
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Manrope'] font-black leading-[1.05] mb-4">
            La carte<br />
            <span className="bg-gradient-to-r from-[#FFC107] to-[#FF6B35] bg-clip-text text-transparent">
              du tireur sportif
            </span>
          </h1>
          <p className="text-base sm:text-lg text-[#C8C8C8] max-w-2xl leading-relaxed">
            Ball-traps de campagne, concours, stands de tir, armureries — toute la France en un coup d&apos;œil. Filtre par catégorie, par région, et trouve le bon spot.
          </p>

          {/* Stat pills */}
          <div className="flex flex-wrap gap-3 mt-6">
            <div className="px-4 py-2 rounded-xl bg-white/5 border border-white/10">
              <span className="text-2xl font-black text-[#FFC107]">{stats.total || 0}</span>
              <span className="ml-2 text-sm text-[#C8C8C8]">évènement{stats.total > 1 ? 's' : ''} à venir</span>
            </div>
            <div className="px-4 py-2 rounded-xl bg-white/5 border border-white/10">
              <span className="text-2xl font-black text-[#FF6B35]">{Object.keys(stats.by_department || {}).length}</span>
              <span className="ml-2 text-sm text-[#C8C8C8]">département{Object.keys(stats.by_department || {}).length > 1 ? 's' : ''} couvert{Object.keys(stats.by_department || {}).length > 1 ? 's' : ''}</span>
            </div>
            {visits !== null && (
              <div
                className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 inline-flex items-center gap-2"
                data-testid="visit-counter"
                title="Nombre de visites sur cette page"
              >
                <Eye className="w-4 h-4 text-[#FFC107]/80" />
                <span className="text-2xl font-black text-white">
                  {visits.toLocaleString('fr-FR')}
                </span>
                <span className="text-sm text-[#C8C8C8]">visite{visits > 1 ? 's' : ''}</span>
              </div>
            )}
            {accountsCount !== null && (
              <div
                className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 inline-flex items-center gap-2"
                data-testid="accounts-counter"
                title="Nombre de comptes ClayPulse"
              >
                <Users className="w-4 h-4 text-[#FF6B35]/80" />
                <span className="text-2xl font-black text-white">
                  {accountsCount.toLocaleString('fr-FR')}
                </span>
                <span className="text-sm text-[#C8C8C8]">membre{accountsCount > 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        </section>

        {/* Category filters + view toggle */}
        <section className="mb-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {Object.entries(CATEGORY_META).map(([key, meta]) => {
              const active = activeCats.has(key);
              return (
                <button
                  key={key}
                  onClick={() => toggleCat(key)}
                  data-testid={`cat-filter-${key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${active ? 'text-white' : 'text-white/50 hover:text-white/80'}`}
                  style={
                    active
                      ? { background: `${meta.color}22`, borderColor: `${meta.color}88` }
                      : { borderColor: 'rgba(255,255,255,0.10)' }
                  }
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color, opacity: active ? 1 : 0.4 }} />
                  {meta.shortLabel}
                </button>
              );
            })}
          </div>
          <div className="sm:ml-auto inline-flex p-1 rounded-full bg-white/5 border border-white/10 self-start" data-testid="map-view-toggle">
            <button
              onClick={() => setMapView('interactive')}
              data-testid="map-view-interactive"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${mapView === 'interactive' ? 'bg-[#FFC107] text-black' : 'text-white/60 hover:text-white'}`}
            >
              <MapIcon className="w-3.5 h-3.5" /> Carte
            </button>
            <button
              onClick={() => setMapView('departments')}
              data-testid="map-view-departments"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${mapView === 'departments' ? 'bg-[#FFC107] text-black' : 'text-white/60 hover:text-white'}`}
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Départements
            </button>
          </div>
        </section>

        {/* Map + Top dept rail */}
        <section className="grid lg:grid-cols-[1fr_320px] gap-6 mb-12">
          <div className="rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/5 p-4 sm:p-6">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-base sm:text-lg font-bold text-white">
                {mapView === 'interactive' ? 'Carte interactive' : 'Carte par département'}
              </h2>
              {selectedDept && (
                <button
                  onClick={() => { setSelectedDept(''); setSelectedDeptName(''); }}
                  className="text-xs text-[#FFC107] hover:underline"
                  data-testid="reset-dept-filter"
                >
                  Réinitialiser ×
                </button>
              )}
            </div>
            {mapView === 'interactive' ? (
              <EventMap events={catFiltered} activeCategories={activeCats} />
            ) : (
              <FranceChoroplethMap
                counts={stats.by_department || {}}
                selectedDept={selectedDept}
                onSelectDept={(code, nom) => { setSelectedDept(code); setSelectedDeptName(nom); }}
              />
            )}
            {stats.total === 0 && !loading && (
              <p className="mt-3 text-xs text-center text-[#888] italic">
                Aucun ball-trap publié pour l'instant — soyez les premiers !
              </p>
            )}
          </div>

          <aside className="space-y-4">
            {/* Top 5 */}
            <div className="rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/5 p-5">
              <h3 className="text-xs uppercase tracking-widest text-[#888] font-bold mb-3">Top départements</h3>
              {topDepts.length === 0 ? (
                <p className="text-sm text-[#888] italic">Aucun évènement pour l'instant</p>
              ) : (
                <ul className="space-y-2">
                  {topDepts.map(([code, n]) => (
                    <li key={code}>
                      <button
                        onClick={() => setSelectedDept(code)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${selectedDept === code ? 'bg-[#FFC107]/15 text-white border border-[#FFC107]/30' : 'hover:bg-white/5 text-[#C8C8C8]'}`}
                        data-testid={`top-dept-${code}`}
                      >
                        <span className="font-semibold">Département {code}</span>
                        <span className="text-[#FFC107] font-bold">{n}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* CTA sidebar */}
            <div className="rounded-2xl overflow-hidden relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-[#FFC107]/20 via-transparent to-[#FF6B35]/20" />
              <div className="relative p-5 bg-[#141414]/60 border border-[#FFC107]/20">
                <h3 className="font-bold text-white mb-1.5">Vous organisez un ball-trap ?</h3>
                <p className="text-xs text-[#C8C8C8] mb-3">Publication gratuite · Validation sous 24 h · Visible par milliers de tireurs.</p>
                <Link
                  to="/ball-trap-campagne/soumettre"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black text-sm font-bold hover:scale-105 transition-transform"
                  data-testid="submit-cta-sidebar"
                >
                  <Plus className="w-4 h-4" /> Déclarer gratuitement
                </Link>
              </div>
            </div>
          </aside>
        </section>

        {/* Events list */}
        <section>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
            <h2 className="text-xl sm:text-2xl font-bold">
              {selectedDept
                ? <>Évènements · <span className="text-[#FFC107]">{selectedDeptName || selectedDept}</span> <span className="text-sm text-[#888] font-normal">({filtered.length})</span></>
                : <>Tous les évènements <span className="text-sm text-[#888] font-normal">({filtered.length})</span></>
              }
            </h2>

            {/* Search bar */}
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888] pointer-events-none" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher — ville, département, discipline…"
                className="w-full pl-10 pr-9 py-2.5 rounded-full bg-white/[0.04] border border-white/10 focus:border-[#FFC107]/50 focus:bg-white/[0.06] outline-none text-sm placeholder:text-[#666] transition-colors"
                data-testid="country-events-search-input"
                aria-label="Rechercher un évènement par ville, département ou discipline"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#888] hover:text-white hover:bg-white/10"
                  aria-label="Effacer la recherche"
                  data-testid="country-events-search-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Active-filters strip */}
          {(selectedDept || debouncedQuery.trim()) && (
            <div className="flex flex-wrap items-center gap-2 mb-4 text-xs text-[#888]" data-testid="active-filters-strip">
              <span>Filtres actifs :</span>
              {selectedDept && (
                <button
                  onClick={() => { setSelectedDept(''); setSelectedDeptName(''); }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FFC107]/10 border border-[#FFC107]/30 text-[#FFC107] hover:bg-[#FFC107]/20"
                  data-testid="active-filter-dept"
                >
                  {selectedDeptName || `Département ${selectedDept}`} <X className="w-3 h-3" />
                </button>
              )}
              {debouncedQuery.trim() && (
                <button
                  onClick={() => setQuery('')}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/80 hover:bg-white/10"
                  data-testid="active-filter-search"
                >
                  « {debouncedQuery.trim()} » <X className="w-3 h-3" />
                </button>
              )}
            </div>
          )}

          {loading ? (
            <p className="text-[#888] text-sm">Chargement…</p>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-10 text-center" data-testid="country-events-empty">
              <p className="text-[#C8C8C8] mb-3">
                {debouncedQuery.trim()
                  ? <>Aucun résultat pour « <span className="text-[#FFC107]">{debouncedQuery.trim()}</span> »{selectedDept ? ' dans ce département' : ''}.</>
                  : selectedDept
                  ? "Aucun évènement prévu dans ce département pour l'instant."
                  : "Aucun évènement publié pour l'instant."}
              </p>
              {debouncedQuery.trim() ? (
                <button
                  onClick={() => { setQuery(''); setSelectedDept(''); setSelectedDeptName(''); }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold text-sm"
                  data-testid="reset-all-filters"
                >
                  <X className="w-4 h-4" /> Réinitialiser les filtres
                </button>
              ) : (
                <Link to="/ball-trap-campagne/soumettre" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FFC107] text-black font-bold text-sm hover:scale-105 transition-transform" data-testid="empty-state-submit-cta">
                  <Plus className="w-4 h-4" /> Publier le premier évènement
                </Link>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="country-events-grid">
              {filtered.map((evt) => (
                <EventCard key={evt.id} evt={evt} onShowPoster={setPosterEvent} />
              ))}
            </div>
          )}
        </section>
      </main>

      <PosterModal evt={posterEvent} onClose={() => setPosterEvent(null)} />
    </div>
  );
}
